import {
  IoChatbubblesOutline,
  IoMailOpenOutline,
  IoMailUnreadOutline,
  IoTrashOutline,
} from "react-icons/io5";
import {
  deleteContact,
  fetchContacts,
  updateContact,
} from "../../redux/contact";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";

import { ContactDataTableExpandable } from "./ContactExpandable";
import DataTable from "react-data-table-component";
import { ErrorBanner } from "../../components/banner";
import { Loading } from "../../components/loader";
import Moment from "moment";
import { Section } from "../../components/section";
import { Status } from "../../components/badges";
import { shadow } from "../../assets/styles/parameters";

export const ContactList = () => {
  const { contacts, error, loading } = useSelector((state) => state.contacts);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchContacts());
  }, [dispatch]);

  // --- ACTIONS ---

  const onDeleteWarning = (row) => {
    if (
      window.confirm(
        `Are you sure you want to delete the message from ${row.name}?`,
      )
    ) {
      // FIX: Ensure we only pass the ID to the delete thunk, not the whole row object
      dispatch(deleteContact(row.id));
    }
  };

  const onToggleReadState = (row) => {
    // Instantly toggle the read status using the update thunk
    dispatch(
      updateContact({
        id: row.id,
        values: { ...row, read: !row.read },
      }),
    );
  };

  // --- STATS CALCULATION ---

  const { totalMessages, unreadCount } = useMemo(() => {
    const unread = contacts.filter((c) => !c.read).length;
    return {
      totalMessages: contacts.length,
      unreadCount: unread,
    };
  }, [contacts]);

  // --- TABLE COLUMNS ---

  const columns = [
    {
      name: "Status",
      // FIX: Removed legacy `metadata.read`
      selector: (row) => row.read,
      cell: (row) => <Status status={row.read ? "Read" : "Unread"} />,
      sortable: true,
      width: "100px",
    },
    {
      name: "Date",
      // FIX: Map to submittedAt based on Swagger spec (fallback to createdDate just in case)
      selector: (row) =>
        Moment(row.submittedAt || row.createdDate).format("MMM DD, YYYY"),
      sortable: true,
      hide: "sm",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      style: { fontWeight: 600, color: "#111827" },
    },
    {
      name: "Email",
      selector: (row) => row.email,
      hide: "md",
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      hide: "sm",
    },
    {
      name: "Actions",
      cell: (row) => (
        <ActionFlex>
          {/* New Feature: Clickable Read/Unread Toggle */}
          <ActionButton
            onClick={() => onToggleReadState(row)}
            title={row.read ? "Mark as Unread" : "Mark as Read"}
            color="#3B5BDB"
            bg="#E6F2FF"
          >
            {row.read ? (
              <IoMailOpenOutline size={18} />
            ) : (
              <IoMailUnreadOutline size={18} />
            )}
          </ActionButton>

          <ActionButton
            onClick={() => onDeleteWarning(row)}
            title="Delete Message"
            color="#dc3545"
            bg="#FDEDEC"
          >
            <IoTrashOutline size={18} />
          </ActionButton>
        </ActionFlex>
      ),
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  if (loading && contacts.length === 0) return <Loading />;

  return (
    <Section>
      {error && <ErrorBanner text={error} />}

      {/* 1. TOP QUICK STATS */}
      <StatsGrid>
        <StatCard delay="0.1s">
          <StatHeader>
            <IconWrapper bg="#F4ECF7" color="#9B59B6">
              <IoChatbubblesOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{totalMessages}</StatValue>
          <StatLabel>Total Messages</StatLabel>
        </StatCard>

        <StatCard delay="0.2s">
          <StatHeader>
            <IconWrapper
              bg={unreadCount > 0 ? "#FFF4E6" : "#E8F8F5"}
              color={unreadCount > 0 ? "#F39C12" : "#2ECA7F"}
            >
              <IoMailUnreadOutline />
            </IconWrapper>
          </StatHeader>
          <StatValue>{unreadCount}</StatValue>
          <StatLabel>
            {unreadCount > 0 ? "Require Attention" : "All Caught Up!"}
          </StatLabel>
        </StatCard>
      </StatsGrid>

      {/* 2. MAIN TABLE */}
      <WidgetCard delay="0.3s">
        <DataTable
          columns={columns}
          data={contacts}
          expandableRows
          expandableRowsComponent={({ data }) => (
            <ContactDataTableExpandable data={data} />
          )}
          striped
          persistTableHead
          pagination
          highlightOnHover
          responsive
          customStyles={tableStyles}
        />
      </WidgetCard>
    </Section>
  );
};

// --- ANIMATIONS & STYLED COMPONENTS ---

const slideUpFade = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
`;

const StatHeader = styled.div`
  display: flex;
  margin-bottom: 12px;
`;

const IconWrapper = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background-color: ${(props) => props.bg};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const StatValue = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px 0;
`;

const StatLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const WidgetCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${shadow.light};
  opacity: 0;
  animation: ${slideUpFade} 0.5s ease-out forwards;
  animation-delay: ${(props) => props.delay || "0s"};
  overflow: hidden;
`;

const ActionFlex = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.color};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${(props) => props.bg};
    transform: translateY(-2px);
  }
`;

// Clean up the DataTable default styling slightly to match the modern theme
const tableStyles = {
  headRow: {
    style: { borderBottomColor: "#f0f0f0", fontWeight: 600, color: "#555" },
  },
  rows: {
    style: {
      minHeight: "56px",
      "&:not(:last-of-type)": { borderBottomColor: "#f0f0f0" },
    },
  },
};

export default ContactList;
